import * as Sentry from '@sentry/react';

/**
 * Materialize a picked File into an owned Blob-backed File.
 *
 * Android Chrome returns File objects from `<input type="file">` that
 * are lazy references into content URIs (Google Photos / Drive /
 * WhatsApp / cloud picker). The reference can go stale minutes later
 * — the file still appears present in state, but any attempt to read
 * its bytes rejects. This materialization reads the bytes immediately
 * at pick time and returns a NEW File backed by an in-memory
 * ArrayBuffer. Downstream consumers (IndexedDB, preview, upload) see
 * an owned copy whose bytes are guaranteed to be readable for the
 * lifetime of the browser session.
 *
 * On failure, captures to Sentry with a `stage` tag and no PII, then
 * throws. Callers decide the UX: picker-time failure maps to the
 * existing inline pick-validation error; hydration-time failure maps
 * to "no photo" (so the builder re-asks); pre-upload failure maps to
 * the LightGate per-person error state.
 */
export type MaterializeStage = 'pick' | 'hydrate' | 'preupload';

export class PhotoMaterializeError extends Error {
  readonly stage: MaterializeStage;
  constructor(stage: MaterializeStage, cause: unknown) {
    super(`photo-materialize: ${stage} read failed`);
    this.name = 'PhotoMaterializeError';
    this.stage = stage;
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

export async function materializeFile(
  file: File,
  stage: MaterializeStage,
  context?: { index?: number; total?: number },
): Promise<File> {
  try {
    const buffer = await file.arrayBuffer();
    // Treat a zero-byte read as unreadable too — some lazy-reference
    // sources resolve to empty on failure instead of throwing.
    if (buffer.byteLength === 0) {
      throw new Error('arrayBuffer returned zero bytes');
    }
    return new File([buffer], file.name, { type: file.type });
  } catch (cause) {
    captureMaterializeFailure(stage, file, cause, context);
    throw new PhotoMaterializeError(stage, cause);
  }
}

/**
 * Verify a stored File is still readable without allocating the full
 * buffer. Used by hydration + pre-upload paths where the File was
 * materialized earlier but could still have become unreadable if a
 * caller handed us a non-materialized File (e.g. a legacy IDB row
 * written before this change set shipped).
 */
export async function isFileReadable(file: File): Promise<boolean> {
  try {
    if (file.size === 0) return false;
    // Read a tiny slice — enough to prove the backing source resolves.
    const slice = file.slice(0, Math.min(16, file.size));
    const buf = await slice.arrayBuffer();
    return buf.byteLength > 0;
  } catch {
    return false;
  }
}

function captureMaterializeFailure(
  stage: MaterializeStage,
  file: File,
  cause: unknown,
  context: { index?: number; total?: number } | undefined,
): void {
  try {
    const err = cause instanceof Error ? cause : new Error(String(cause));
    const tags: Record<string, string | number> = {
      stage,
      file_size: file.size,
      file_type: file.type.length === 0 ? 'unknown' : file.type,
    };
    if (context?.index !== undefined) tags.index = context.index;
    if (context?.total !== undefined) tags.total = context.total;
    Sentry.captureException(err, { tags });
  } catch {
    // Sentry capture never blocks the caller.
  }
}

export function captureReadableFailure(
  stage: MaterializeStage,
  file: File,
  context?: { index?: number; total?: number },
): void {
  try {
    const err = new Error(`photo-readable: ${stage} readability check failed`);
    const tags: Record<string, string | number> = {
      stage,
      file_size: file.size,
      file_type: file.type.length === 0 ? 'unknown' : file.type,
    };
    if (context?.index !== undefined) tags.index = context.index;
    if (context?.total !== undefined) tags.total = context.total;
    Sentry.captureException(err, { tags });
  } catch {
    // Non-fatal.
  }
}

export function captureUploadPutFailure(params: {
  readonly httpStatus: number | 'network';
  readonly fileSize: number;
  readonly fileType: string;
  readonly index: number;
  readonly total: number;
}): void {
  try {
    const err = new Error(`photo-upload: PUT to signed URL failed`);
    const tags: Record<string, string | number> = {
      stage: 'put',
      http_status: params.httpStatus,
      file_size: params.fileSize,
      file_type: params.fileType.length === 0 ? 'unknown' : params.fileType,
      index: params.index,
      total: params.total,
    };
    Sentry.captureException(err, { tags });
  } catch {
    // Non-fatal.
  }
}
